import { DiffCounts, DiffLine } from '@components/patterns/Diff/parse';

import css from './styles.module.css';

interface LineTextProps {
  line: DiffLine;
}
function LineText({ line }: LineTextProps) {
  switch (line.type) {
    case 'header': {
      const from = `-${line.from.line},${line.from.count}`;
      const to = `+${line.to.line},${line.to.count}`;
      return (
        <span className={css.lineText}>
          @@ {from} {to} @@
        </span>
      );
    }

    case 'common':
      return <span className={css.lineText}>{line.content}</span>;

    case 'added':
    case 'removed':
      return <span className={css.lineText}>{line.content}</span>;
  }
}

interface LineSymbolProps {
  line: DiffLine;
  counts: DiffCounts;
}
function LineSymbol({ line, counts }: LineSymbolProps) {
  if (line.type === 'header') {
    return null;
  } else if (counts.additions === 0 && counts.removals === 0) {
    return null;
  } else if (line.type === 'added') {
    return <span className={css.lineSymbol}>+</span>;
  } else if (line.type === 'removed') {
    return <span className={css.lineSymbol}>-</span>;
  } else {
    return <span className={css.lineSymbol}>&nbsp;</span>;
  }
}

interface LineContentProps {
  line: DiffLine;
  counts: DiffCounts;
}
function LineContent({ line, counts }: LineContentProps) {
  return (
    <span className={css.lineContent}>
      <LineSymbol line={line} counts={counts} />

      <LineText line={line} />
    </span>
  );
}

interface LineNumberProps {
  line: DiffLine;
  counts: DiffCounts;
}
function LineNumber({ line, counts }: LineNumberProps) {
  if (line.type === 'common') {
    const showUpdatedLineNumber = counts.additions > 0 || counts.removals > 0;
    return (
      <>
        <span className={css.lineNumber} data-id="line-number-original">
          {line.lines.from}
        </span>
        {showUpdatedLineNumber && (
          <span className={css.lineNumber} data-id="line-number-updated">
            {line.lines.to}
          </span>
        )}
      </>
    );
  }

  if (line.type === 'added') {
    const showOriginalLineNumber = counts.removals > 0 || counts.common > 0;
    return (
      <>
        {showOriginalLineNumber && (
          <span className={css.lineNumber} data-id="line-number-original" />
        )}
        <span className={css.lineNumber} data-id="line-number-updated">
          {line.lines.to}
        </span>
      </>
    );
  }

  if (line.type === 'removed') {
    const showUpdatedLineNumber = counts.additions > 0 || counts.common > 0;
    return (
      <>
        <span className={css.lineNumber} data-id="line-number-original">
          {line.lines.from}
        </span>
        {showUpdatedLineNumber && (
          <span className={css.lineNumber} data-id="line-number-updated" />
        )}
      </>
    );
  }

  return null;
}

export interface LineProps {
  line: DiffLine;
  counts: DiffCounts;
}
export function Line({ line, counts }: LineProps) {
  return (
    <span className={css.line} data-type={line.type}>
      <LineNumber line={line} counts={counts} />
      <LineContent line={line} counts={counts} />
    </span>
  );
}
